import type { ActionOptionCommon, ActionValues, StepValues, Workflow } from '@repo/common';

import { ActionType, Step } from '@repo/common';

import { REDIRECT_DELAY_ERROR_MS, REDIRECT_DELAY_SUCCESS_MS } from '@/utils/redirect-timeouts';

export type StepWorkflowOptions = {
  component?: string;
  /** Optional step-specific payload from BFF (e.g. multirange `challengeSequence`). Threaded into `NextStep.data` so capture components can read it without extra round-trips. */
  data?: Record<string, unknown>;
  skippable?: boolean;
  unverifiedUrl?: string;
  verifiedUrl?: string;
};

type StepMapping = {
  actionType: ActionValues;
  component?: string;
  skippable?: boolean;
  value: StepValues;
};

const STEP_MAP: Record<string, StepMapping> = {
  info: { value: Step.INTRO, actionType: ActionType.PROCESS, component: 'Info' },
  mobile_redirect: {
    value: Step.MOBILE_REDIRECT,
    actionType: ActionType.PROCESS,
    skippable: true,
    component: 'MobileRedirect',
  },
  document_front: { value: Step.DOCUMENT_FRONT, actionType: ActionType.PROCESS, component: 'DocumentCapture' },
  document_back: {
    value: Step.DOCUMENT_BACK,
    actionType: ActionType.PROCESS,
    skippable: true,
    component: 'DocumentCapture',
  },
  selfie: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'SelfieCapture' },
  /** Manifest `WORKFLOW_UI_STEP_ACTION` ids from BFF (`apps/bff/src/lib/action-to-component.ts`). */
  face_capture: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'SelfieCapture' },
  smile_capture: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'SmileLiveness' },
  multirange_capture: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'MultiRangeLiveness' },
  // Manifest-driven steps (not represented in `@repo/common` Step enum) still need stable ids
  // so FE can transition based solely on `nextStep.step`.
  smile_liveness: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'SmileLiveness' },
  multi_range: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'MultiRangeLiveness' },
  nfc_capture: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'NfcCapture' },
  // SAIDVU-499/501 palm steps. Value reuses the FACE bucket (a coarse progress marker, like the
  // other manifest capture steps above); rendering is by `component` via the FE COMPONENT_REGISTRY.
  both_palms_capture: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'PalmCapture' },
  single_palm_capture: { value: Step.FACE, actionType: ActionType.PROCESS, component: 'PalmCapture' },
  evaluate: { value: Step.FACE, actionType: ActionType.EVALUATE, component: 'Loading' },
  complete: { value: Step.SUCCESS, actionType: ActionType.REDIRECT, component: 'Result' },
  enrolment_pending: { value: Step.SUCCESS, actionType: ActionType.PROCESS, component: 'EnrolmentPendingReview' },
  fail: { value: Step.FAIL, actionType: ActionType.REDIRECT, component: 'VerificationFailed' },
};

const COMPONENT_TO_STEP: Record<string, StepValues> = {
  Info: Step.INTRO,
  MobileRedirect: Step.MOBILE_REDIRECT,
  DocumentCapture: Step.DOCUMENT_FRONT,
  SelfieCapture: Step.FACE,
  SmileLiveness: Step.FACE,
  MultiRangeLiveness: Step.FACE,
  NfcCapture: Step.FACE,
  PalmCapture: Step.FACE,
  LivenessCheck: Step.FACE,
  Loading: Step.FACE,
  Result: Step.SUCCESS,
  EnrolmentPendingReview: Step.SUCCESS,
  VerificationFailed: Step.FAIL,
};

const COMPONENT_TO_ACTION: Record<string, ActionValues> = {
  Info: ActionType.PROCESS,
  MobileRedirect: ActionType.PROCESS,
  DocumentCapture: ActionType.PROCESS,
  SelfieCapture: ActionType.PROCESS,
  SmileLiveness: ActionType.PROCESS,
  MultiRangeLiveness: ActionType.PROCESS,
  NfcCapture: ActionType.PROCESS,
  PalmCapture: ActionType.PROCESS,
  LivenessCheck: ActionType.PROCESS,
  Loading: ActionType.EVALUATE,
  Result: ActionType.REDIRECT,
  EnrolmentPendingReview: ActionType.PROCESS,
  VerificationFailed: ActionType.REDIRECT,
};

/** BFF manifest UI step ids reuse the same action token with `_2`, `_3`, … when the manifest repeats an action. */
function resolveStepMapping(step: string): StepMapping | undefined {
  return STEP_MAP[step] ?? STEP_MAP[step.replace(/_\d+$/, '')];
}

function buildWorkflow(
  stepValue: StepValues,
  actionType: ActionValues,
  component: string | undefined,
  isSkippable: boolean | undefined,
  step: string,
  opts: Pick<StepWorkflowOptions, 'data' | 'unverifiedUrl' | 'verifiedUrl'>,
): Workflow {
  const dataField = opts.data ? { data: opts.data } : {};

  if (isSkippable) {
    return {
      nextStep: {
        action: {
          type: ActionType.OPTIONS,
          options: [{ type: ActionType.SKIP }, { type: ActionType.CONTINUE }],
        },
        component,
        value: stepValue,
        ...dataField,
      },
    } as Workflow;
  }

  if (actionType === ActionType.REDIRECT) {
    const url = step === 'complete' ? (opts.verifiedUrl ?? '') : (opts.unverifiedUrl ?? '');
    const result = step === 'complete' ? 'success' : 'error';
    const redirectTimeout = step === 'complete' ? REDIRECT_DELAY_SUCCESS_MS : REDIRECT_DELAY_ERROR_MS;

    return {
      nextStep: {
        action: {
          data: { url, payload: { result }, redirectTimeout },
          type: ActionType.REDIRECT,
        },
        component,
        value: stepValue,
        ...dataField,
      },
    } as Workflow;
  }

  return {
    nextStep: {
      action: {
        data: {},
        type: actionType as ActionOptionCommon['type'],
      },
      component,
      value: stepValue,
      ...dataField,
    },
  } as Workflow;
}

/**
 * Maps server `nextStep` (client step id + optional component) to app workflow state.
 */
export function stepToWorkflow(step: string, opts: StepWorkflowOptions = {}): Workflow | null {
  const { component, data, skippable: passedSkippable, unverifiedUrl, verifiedUrl } = opts;

  const mapping = resolveStepMapping(step);

  if (mapping) {
    const isSkippable = passedSkippable ?? mapping.skippable;
    const resolvedComponent = component ?? mapping.component;

    return buildWorkflow(mapping.value, mapping.actionType, resolvedComponent, isSkippable, step, {
      data,
      unverifiedUrl,
      verifiedUrl,
    });
  }

  if (component && COMPONENT_TO_STEP[component]) {
    const stepValue = COMPONENT_TO_STEP[component];
    const actionType = COMPONENT_TO_ACTION[component];
    const isSkippable = passedSkippable;

    return buildWorkflow(stepValue, actionType, component, isSkippable, step, { data, unverifiedUrl, verifiedUrl });
  }

  return null;
}

/** @deprecated Use stepToWorkflow */
export const bffStepToWorkflow = stepToWorkflow;

/**
 * Build a `Workflow` from a BFF submit response, threading the optional `nextStep.data`
 * payload (e.g. multirange `challengeSequence`, NFC `challenge`) through to the workflow's
 * `NextStep.data`. Centralizes what otherwise had to be inlined at every submit call site.
 */
export function nextWorkflowFromSubmit(
  response: { nextStep: { data?: Record<string, unknown>; step: string } },
  opts: Omit<StepWorkflowOptions, 'data'> = {},
): Workflow | null {
  return stepToWorkflow(response.nextStep.step, { ...opts, data: response.nextStep.data });
}
